import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  X,
  ImagePlus,
  Zap,
  Flame,
  Waves,
  ArrowDownToLine,
  HardHat,
  Wrench,
  Truck,
  Volume2,
  CloudSun,
  OctagonAlert,
  Bug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiPost } from "@/lib/api";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { useCurrentProject } from "@/hooks/use-current-project";
import { useFileUpload } from "@/hooks/use-file-upload";
import { useVoiceInput } from "@/hooks/use-voice-input";
import { FieldIconGrid, type FieldIconGridItem } from "@/design-system/field-icon-grid";
import {
  PhotoAnnotator,
  type HazardAnnotation,
} from "@/components/hazards/photo-annotator";
import {
  SeverityCardPicker,
  type HazardSeverity,
} from "@/components/hazards/severity-card-picker";
import { cn } from "@/lib/utils";
import { Mic, Square } from "lucide-react";

/**
 * Phase 5: Camera-first hazard reporting flow.
 *
 * Flow steps: capture → annotate → categorize → severity → describe → submitting
 * Target: ≤4 taps from camera open to submit.
 */
export const Route = createFileRoute("/_authenticated/hazards/new")({
  component: NewHazardPage,
});

type Step = "capture" | "annotate" | "categorize" | "severity" | "describe" | "submitting";

interface CapturedPhoto {
  id: string;
  file: File;
  previewUrl: string;
  /** Final file id (after upload). */
  fileId?: string;
  annotations?: HazardAnnotation[];
  annotated?: boolean;
}

// ---------------------------------------------------------------------------
// Hazard category catalog — pulled from the FLHA hazards icons for parity.
// Maps to the `hazard_catalog` table via `code` so the free-form
// `categoryHazardId` on the backend works whether or not the catalog row
// exists. We pass `code` through as the `categoryHazardId` label.
// ---------------------------------------------------------------------------
const CATEGORY_ITEMS: FieldIconGridItem[] = [
  { key: "electrical", label: "Electrical", icon: <Zap className="h-7 w-7" />, critical: true },
  { key: "fire_hot_work", label: "Fire / Hot Work", icon: <Flame className="h-7 w-7" />, critical: true },
  { key: "slip_trip_fall", label: "Slip / Trip", icon: <Waves className="h-7 w-7" /> },
  { key: "working_at_heights", label: "Working at Heights", icon: <ArrowDownToLine className="h-7 w-7" />, critical: true },
  { key: "struck_by", label: "Struck / Overhead", icon: <HardHat className="h-7 w-7" /> },
  { key: "mechanical", label: "Mechanical", icon: <Wrench className="h-7 w-7" /> },
  { key: "mobile_equipment", label: "Mobile Equipment", icon: <Truck className="h-7 w-7" /> },
  { key: "biological", label: "Biological", icon: <Bug className="h-7 w-7" /> },
  { key: "noise", label: "Noise", icon: <Volume2 className="h-7 w-7" /> },
  { key: "weather", label: "Weather", icon: <CloudSun className="h-7 w-7" /> },
  { key: "other", label: "Other", icon: <OctagonAlert className="h-7 w-7" /> },
];

function NewHazardPage() {
  const navigate = useNavigate();
  const { coords } = useCurrentLocation();
  const { project } = useCurrentProject();
  const { upload, isUploading } = useFileUpload();

  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("capture");
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [annotateIdx, setAnnotateIdx] = useState<number>(0);
  const [category, setCategory] = useState<string[]>([]);
  const [severity, setSeverity] = useState<HazardSeverity | null>(null);
  const [description, setDescription] = useState("");

  // Clean up object URLs on unmount.
  useEffect(() => {
    return () => {
      photos.forEach((p) => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {
          /* noop */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Capture handlers ----
  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    const id = Math.random().toString(36).slice(2);
    setPhotos((prev) => [...prev, { id, file, previewUrl }]);
    e.target.value = "";
    // Auto-advance to annotate on first capture.
    setStep((prev) => (prev === "capture" ? "annotate" : prev));
    setAnnotateIdx(photos.length);
  }

  function removePhoto(id: string) {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch {
          /* noop */
        }
      }
      return prev.filter((p) => p.id !== id);
    });
  }

  // ---- Annotation save ----
  async function handleAnnotateSave(result: { blob: Blob; annotations: HazardAnnotation[] }) {
    const photo = photos[annotateIdx];
    if (!photo) return;
    try {
      const annotatedFile = new File([result.blob], photo.file.name, {
        type: "image/jpeg",
      });
      const up = await upload(annotatedFile, {
        fileName: annotatedFile.name,
        mimeType: annotatedFile.type,
        purpose: "photo",
      });
      setPhotos((prev) =>
        prev.map((p, i) =>
          i === annotateIdx
            ? { ...p, fileId: up.fileId, annotations: result.annotations, annotated: true }
            : p
        )
      );
      setStep("categorize");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast.error(msg);
    }
  }

  async function handleAnnotateSkip() {
    const photo = photos[annotateIdx];
    if (!photo) return;
    // Upload original photo as-is.
    try {
      const up = await upload(photo.file, {
        fileName: photo.file.name,
        mimeType: photo.file.type,
        purpose: "photo",
      });
      setPhotos((prev) =>
        prev.map((p, i) =>
          i === annotateIdx ? { ...p, fileId: up.fileId, annotated: false } : p
        )
      );
      setStep("categorize");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast.error(msg);
    }
  }

  // ---- Submit ----
  const submitMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        projectId: project?.id,
        severity: severity!,
        description: description || undefined,
        photoFileIds: photos
          .map((p) => p.fileId)
          .filter((id): id is string => typeof id === "string"),
        categoryHazardId: category[0] || undefined,
        locationLat: coords?.lat,
        locationLng: coords?.lon,
      };
      return apiPost<{ data: { id: string } }>("/api/incidents/quick-hazard", payload);
    },
    onSuccess: () => {
      toast.success("Hazard reported");
      navigate({ to: "/hazards" }).catch(() => {});
    },
    onError: (err: Error) => {
      setStep("describe");
      toast.error(err.message || "Failed to submit hazard");
    },
  });

  function handleSubmit() {
    if (!severity) {
      toast.error("Please pick a severity");
      setStep("severity");
      return;
    }
    if (photos.length === 0) {
      toast.error("Please capture at least one photo");
      setStep("capture");
      return;
    }
    if (photos.some((p) => !p.fileId)) {
      toast.error("Still uploading photos…");
      return;
    }
    setStep("submitting");
    submitMutation.mutate();
  }

  // ---- Header ----
  function goBack() {
    switch (step) {
      case "annotate":
        setStep("capture");
        return;
      case "categorize":
        setStep(photos.length > 0 ? "annotate" : "capture");
        return;
      case "severity":
        setStep("categorize");
        return;
      case "describe":
        setStep("severity");
        return;
      default:
        navigate({ to: "/" }).catch(() => {});
    }
  }

  const stepTitle = useMemo(() => {
    switch (step) {
      case "capture":
        return "Capture";
      case "annotate":
        return "Mark up";
      case "categorize":
        return "Category";
      case "severity":
        return "Severity";
      case "describe":
        return "Details";
      case "submitting":
        return "Submitting…";
      default:
        return "";
    }
  }, [step]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 px-2 py-1.5 text-sm text-text-secondary"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
          Back
        </button>
        <h1 className="text-sm font-bold">{stepTitle}</h1>
        <div className="w-[64px]" />
      </div>

      {/* Step body */}
      <div className="flex-1 min-h-0 flex flex-col p-4">
        {step === "capture" && (
          <CaptureStep
            photos={photos}
            onCameraClick={() => cameraRef.current?.click()}
            onUploadClick={() => fileRef.current?.click()}
            onRemove={removePhoto}
            onContinue={() => setStep("categorize")}
            onAnnotate={(idx) => {
              setAnnotateIdx(idx);
              setStep("annotate");
            }}
          />
        )}

        {step === "annotate" && photos[annotateIdx] && (
          <PhotoAnnotator
            src={photos[annotateIdx].previewUrl}
            onSave={handleAnnotateSave}
            onSkip={handleAnnotateSkip}
          />
        )}

        {step === "categorize" && (
          <CategorizeStep
            selected={category}
            onChange={setCategory}
            onContinue={() => setStep("severity")}
          />
        )}

        {step === "severity" && (
          <SeverityStep
            value={severity}
            onChange={setSeverity}
            onContinue={() => setStep("describe")}
          />
        )}

        {step === "describe" && (
          <DescribeStep
            value={description}
            onChange={setDescription}
            submitting={submitMutation.isPending || isUploading}
            onSubmit={handleSubmit}
          />
        )}

        {step === "submitting" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-paris-red" />
            <p className="text-sm text-muted-foreground">Submitting hazard report…</p>
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileInputChange}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Capture
// ---------------------------------------------------------------------------
function CaptureStep({
  photos,
  onCameraClick,
  onUploadClick,
  onRemove,
  onContinue,
  onAnnotate,
}: {
  photos: CapturedPhoto[];
  onCameraClick: () => void;
  onUploadClick: () => void;
  onRemove: (id: string) => void;
  onContinue: () => void;
  onAnnotate: (idx: number) => void;
}) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1">
        {photos.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center h-full gap-4 px-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-paris-red/10">
              <Camera className="h-10 w-10 text-paris-red" />
            </div>
            <div className="text-center">
              <p className="text-lg font-bold font-display">Snap the hazard</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                Tap "Camera" to capture what you see. You can add more after.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {photos.map((p, idx) => (
              <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-black/5">
                <button
                  type="button"
                  onClick={() => onAnnotate(idx)}
                  className="block h-full w-full"
                  aria-label="Annotate photo"
                >
                  <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                </button>
                {p.annotated && (
                  <div className="absolute left-1 top-1 flex h-6 items-center gap-1 rounded-full bg-emerald-600 px-2 text-[10px] font-bold text-white">
                    <Check className="h-3 w-3" />
                    Marked
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(p.id);
                  }}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"
                  aria-label="Remove photo"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 pt-4">
        <div className="flex gap-2">
          <Button
            type="button"
            size="lg"
            className="flex-1 h-14 gap-2 bg-paris-red hover:bg-paris-red-dark"
            onClick={onCameraClick}
          >
            <Camera className="h-5 w-5" />
            {photos.length === 0 ? "Camera" : "Add more"}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="outline"
            className="flex-1 h-14 gap-2"
            onClick={onUploadClick}
          >
            <ImagePlus className="h-5 w-5" />
            Upload
          </Button>
        </div>
        {photos.length > 0 && (
          <Button
            type="button"
            size="lg"
            variant="secondary"
            className="h-12"
            onClick={onContinue}
          >
            Continue with {photos.length} photo{photos.length === 1 ? "" : "s"}
            <ChevronRight className="h-5 w-5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Categorize
// ---------------------------------------------------------------------------
function CategorizeStep({
  selected,
  onChange,
  onContinue,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col gap-4 min-h-0">
      <p className="text-sm text-muted-foreground">
        Pick the hazard type. You can skip if nothing fits.
      </p>
      <div className="flex-1 overflow-y-auto">
        <FieldIconGrid
          items={CATEGORY_ITEMS}
          selected={selected}
          onSelectedChange={(next) => {
            // Single-select: keep only the most recently toggled item.
            const added = next.find((k) => !selected.includes(k));
            onChange(added ? [added] : []);
          }}
          columns={2}
        />
      </div>
      <Button
        type="button"
        size="lg"
        className="h-14"
        onClick={onContinue}
      >
        {selected.length > 0 ? "Continue" : "Skip category"}
        <ChevronRight className="h-5 w-5 ml-1" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Severity
// ---------------------------------------------------------------------------
function SeverityStep({
  value,
  onChange,
  onContinue,
}: {
  value: HazardSeverity | null;
  onChange: (next: HazardSeverity) => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col gap-4 min-h-0">
      <p className="text-sm text-muted-foreground">
        How serious is this? High and Critical notify site leads immediately.
      </p>
      <div className="flex-1">
        <SeverityCardPicker value={value} onChange={onChange} />
      </div>
      <Button
        type="button"
        size="lg"
        className="h-14"
        disabled={!value}
        onClick={onContinue}
      >
        Continue
        <ChevronRight className="h-5 w-5 ml-1" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Describe (optional) + submit
// ---------------------------------------------------------------------------
function DescribeStep({
  value,
  onChange,
  submitting,
  onSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const { isSupported, isListening, transcript, start, stop } = useVoiceInput();

  // Push transcript updates into the text field.
  useEffect(() => {
    if (!transcript) return;
    onChange(transcript);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  return (
    <div className="flex-1 flex flex-col gap-4 min-h-0">
      <p className="text-sm text-muted-foreground">
        Add details (optional). Tap the mic for voice input.
      </p>
      <div className="relative flex-1 flex flex-col">
        <textarea
          className={cn(
            "w-full flex-1 min-h-[160px] rounded-lg border border-input bg-background p-3 text-base",
            "focus:outline-none focus:ring-2 focus:ring-ring"
          )}
          placeholder="What's the hazard? Where exactly?"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {isSupported && (
          <button
            type="button"
            onClick={() => (isListening ? stop() : start())}
            aria-label={isListening ? "Stop voice input" : "Start voice input"}
            aria-pressed={isListening}
            className={cn(
              "absolute right-3 bottom-3 flex h-12 w-12 items-center justify-center rounded-full border-2 shadow-sm transition-colors",
              isListening
                ? "bg-paris-red border-paris-red text-white animate-pulse"
                : "bg-card border-border text-text-secondary hover:border-paris-red hover:text-paris-red-text"
            )}
          >
            {isListening ? <Square className="h-5 w-5" fill="currentColor" /> : <Mic className="h-5 w-5" />}
          </button>
        )}
      </div>
      <Button
        type="button"
        size="lg"
        className="h-14 bg-paris-red hover:bg-paris-red-dark"
        disabled={submitting}
        onClick={onSubmit}
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Submitting…
          </>
        ) : (
          <>
            Submit hazard
            <Check className="h-5 w-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
}
